import type { BuyBoxRole } from "@/app/new/ec/product/[basePath]/sections/buy-box/buyBoxOptions";
import type {
  SellerOptionFilter,
  SellerOptionSort,
} from "@/app/new/ec/product/[basePath]/sections/vendor-options/sellerOptions";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/** What the product page was rendered with, so the seller options drawer can show the same offers. */
export interface SellerOptionsPage {
  mpId: number;
  /** Quantity `offers` are priced for. */
  quantity: number;
  /** In-stock offers in neo's recommended order. */
  offers: VendorOptionState[];
  isManufacturerSponsor: boolean;
  /** Vendors the signed-in shopper has bought from before. */
  vendorPurchases: VendorPurchase[];
  /** Leaf category name, which the cart needs with every item. */
  categoryName: string;
  /** The manufacturer or category requires a license, whatever the vendor says. */
  productRequiresLicense: boolean;
  /** The seller the shopper arrived for through ?vid or ?tsid, whose box leads the buy box; null without one. */
  featuredVendorId: number | null;
  /** Which of the buy box's authorized, lowest price, and fastest shipping boxes show, in order. */
  buyBoxRoleOrder: BuyBoxRole[];
  /** Zip the page was rendered for, or null when the server couldn't resolve one. */
  deliveryZipCode: string | null;
}

export interface VendorPurchase {
  vendorId: number;
  /** Orders that included the vendor; null when only neo's list of vendors was available. */
  totalPurchases: number | null;
  /** MM/DD/YY; null when only neo's list of vendors was available. */
  lastPurchasedOn: string | null;
}

export interface SellerOptionsSliceState {
  /** Null until a product page opens. */
  page: SellerOptionsPage | null;
  /** Quantity the shopper picked; offers are re-priced for it. */
  quantity: number;
  filters: SellerOptionFilter[];
  sort: SellerOptionSort;
  /** The buy box role the shopper picked, or null to take the first box that can sell the quantity. */
  selectedRole: BuyBoxRole | null;
  /** A delivery zip the shopper picked after the page rendered; the offers are re-priced for it. */
  deliveryZipCode: string | null;
}

const initialState: SellerOptionsSliceState = {
  page: null,
  quantity: 1,
  filters: [],
  sort: "recommended",
  selectedRole: null,
  deliveryZipCode: null,
};

// Quantity, filters, sort, the buy box pick, and a picked zip belong to the product page they were picked on:
// opening another product, or coming back to this one after leaving it, starts over (the page already renders
// for the zip in the shopper's cookies), while the same product rendering again keeps them.
export const sellerOptionsSlice = createSlice({
  name: "sellerOptionsSlice",
  initialState,
  reducers: {
    sellerOptionsPageClosed: () => initialState,
    sellerOptionsPageOpened: (state, action: PayloadAction<SellerOptionsPage>) => {
      if (state.page?.mpId !== action.payload.mpId) {
        state.quantity = action.payload.quantity;
        state.filters = [];
        state.sort = "recommended";
        state.selectedRole = null;
        state.deliveryZipCode = null;
      }
      state.page = action.payload;
    },
    sellerOptionsQuantityChanged: (state, action: PayloadAction<number>) => {
      state.quantity = action.payload;
    },
    sellerOptionsFilterToggled: (state, action: PayloadAction<SellerOptionFilter>) => {
      state.filters = state.filters.includes(action.payload)
        ? state.filters.filter((filter) => filter !== action.payload)
        : [...state.filters, action.payload];
    },
    sellerOptionsFiltersCleared: (state) => {
      state.filters = [];
    },
    sellerOptionsSortChanged: (state, action: PayloadAction<SellerOptionSort>) => {
      state.sort = action.payload;
    },
    buyBoxRoleSelected: (state, action: PayloadAction<BuyBoxRole>) => {
      state.selectedRole = action.payload;
    },
    sellerOptionsDeliveryZipCodeChanged: (state, action: PayloadAction<string | null>) => {
      state.deliveryZipCode = action.payload;
    },
  },
});

export const {
  sellerOptionsPageOpened,
  sellerOptionsPageClosed,
  sellerOptionsQuantityChanged,
  sellerOptionsFilterToggled,
  sellerOptionsFiltersCleared,
  sellerOptionsSortChanged,
  buyBoxRoleSelected,
  sellerOptionsDeliveryZipCodeChanged,
} = sellerOptionsSlice.actions;

export default sellerOptionsSlice;
